import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import UnifiedTimeline from '../components/UnifiedTimeline'
import { deriveDriverTripState, isTerminalStopStatus } from './driverTripState'

function mapStopAddress(stop: any) {
  const snapshot = stop?.placeSnapshot ?? {}
  const city = snapshot.city ?? ''
  const state = snapshot.state ?? ''
  const zip = snapshot.zip ?? ''
  const address = snapshot.address1 ?? snapshot.name ?? ''
  return [address, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ')
}

function mapsHref(address: string) {
  const encoded = encodeURIComponent(address)
  return `https://www.google.com/maps/search/?api=1&query=${encoded}`
}

function displayVin(vin?: string | null) {
  const raw = String(vin ?? '')
  if (!raw) return 'VIN unavailable'
  if (raw.length <= 6) return raw
  return `${raw.slice(0, 3)}***${raw.slice(-3)}`
}

export default function DriverAppPage() {
  const queryClient = useQueryClient()
  const [selectedInspectionUnitId, setSelectedInspectionUnitId] = useState('')
  const [inspectionError, setInspectionError] = useState<string | null>(null)

  const { data: todayTripResponse } = useQuery({
    queryKey: ['driver-today-trip'],
    queryFn: async () => (await api.driver.todayTrip()).data,
    refetchInterval: 10000,
  })

  const { data: todayTimelineResponse } = useQuery({
    queryKey: ['driver-today-timeline'],
    queryFn: async () => (await api.driver.todayTimeline()).data,
    refetchInterval: 10000,
  })

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['driver-app-jobs'],
    queryFn: async () => (await api.jobs.list({ viewer: 'driver' })).data,
    refetchInterval: 10000,
  })

  const activeJobs = jobs.filter(j => !['DELIVERED', 'CANCELLED'].includes(j.status))
  const completedJobs = jobs.filter(j => ['DELIVERED', 'CANCELLED'].includes(j.status))
  const todayTrip = todayTripResponse?.trip ?? null
  const todayJob = jobs.find((job) => job.id === todayTrip?.jobId) ?? activeJobs[0] ?? completedJobs[0] ?? null
  const stops = todayTripResponse?.stops ?? []
  const units = todayTripResponse?.units ?? []
  const timeline = todayTimelineResponse?.timeline ?? []
  const tripState = useMemo(() => deriveDriverTripState(todayTrip, stops, units), [todayTrip, stops, units])
  const currentStop = tripState.currentStop
  const currentAddress = mapStopAddress(currentStop)
  const trailerWarnings = Array.isArray(todayTripResponse?.trailerPlan?.warnings) ? todayTripResponse?.trailerPlan?.warnings : []
  const inspectionUnits = useMemo(
    () => units.filter((unit) => String(unit.unit_move_id ?? '').trim()),
    [units],
  )
  const inspectionSelectionRequired = inspectionUnits.length > 1
  const selectedInspectionUnit = inspectionSelectionRequired
    ? inspectionUnits.find((unit) => unit.unit_move_id === selectedInspectionUnitId) ?? null
    : inspectionUnits[0] ?? null
  const currentStopStatus = String(currentStop?.stopStatus || '').trim().toUpperCase()
  const isPickup = String(currentStop?.stopType || '').toUpperCase() === 'PICKUP'
  const isDelivery = String(currentStop?.stopType || '').toUpperCase() === 'DELIVERY'
  const contactPhone = String(currentStop?.contactPhone || '').trim()
  const canCall = contactPhone.length > 0
  const unitCountAtStop = units.length
  const showMarkArrival = Boolean(currentStop) && !['ARRIVED', 'PICKUP_IN_PROGRESS', 'DELIVERY_IN_PROGRESS'].includes(currentStopStatus) && !isTerminalStopStatus(currentStop)
  const showStartPickup = isPickup && ['PENDING', 'ARRIVED'].includes(currentStopStatus)
  const showOpenInspection = Boolean(currentStop) && (isPickup || isDelivery) && !isTerminalStopStatus(currentStop)
  const showConfirmLoaded = isPickup && ['ARRIVED', 'PICKUP_IN_PROGRESS'].includes(currentStopStatus)
  const showStartDelivery = isDelivery && ['PENDING', 'ARRIVED'].includes(currentStopStatus)
  const showCompleteDelivery = isDelivery && ['ARRIVED', 'DELIVERY_IN_PROGRESS'].includes(currentStopStatus)
  const tripStatus = String(todayTrip?.status || '').trim().toUpperCase()
  const showAcknowledge = ['PLANNED', 'DISPATCHED', 'ASSIGNED'].includes(tripStatus)
  const showEnRoute = ['ACKNOWLEDGED', 'EN_ROUTE'].includes(tripStatus)
  const podStatus = todayJob?.proofOfDeliveryUploadedAt
    ? 'Uploaded'
    : todayJob?.status === 'WAITING_POD'
      ? 'Pending'
      : todayJob?.proofOfDeliveryUrl
        ? 'Available'
        : 'Not available'

  const updateAppointmentMutation = useMutation({
    mutationFn: async (input: { stopId: string; appointmentStatus: 'REQUESTED' | 'CONFIRMED' | 'RESCHEDULE_REQUESTED' | 'MISSED' | 'COMPLETED' }) => {
      await api.driver.updateTodayTripStopAppointment(input.stopId, {
        appointmentStatus: input.appointmentStatus,
        appointmentConfirmed: input.appointmentStatus === 'CONFIRMED' || input.appointmentStatus === 'COMPLETED',
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['driver-today-trip'] })
      await queryClient.invalidateQueries({ queryKey: ['driver-today-timeline'] })
    },
  })

  const runStopActionMutation = useMutation({
    mutationFn: async (input: { stopId: string; action: 'ACKNOWLEDGE' | 'EN_ROUTE' | 'MARK_ARRIVAL' | 'START_PICKUP' | 'LOCK_PICKUP_INSPECTION' | 'CONFIRM_LOADED' | 'REPORT_CONDITION_ISSUE' | 'START_DELIVERY' | 'COMPLETE_DELIVERY' | 'MARK_UNABLE_TO_COMPLETE'; notes?: string; unitMoveId?: string | null }) => {
      await api.driver.performTodayTripStopAction(input.stopId, {
        action: input.action,
        notes: input.notes,
        unitMoveId: input.unitMoveId,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['driver-today-trip'] })
      await queryClient.invalidateQueries({ queryKey: ['driver-today-timeline'] })
    },
  })

  const openInspectionMutation = useMutation({
    mutationFn: async () => {
      if (!currentStop?.id) throw new Error('No actionable stop available for inspection.')
      const unitId = selectedInspectionUnit?.unit_move_id ?? null
      if (inspectionSelectionRequired && !unitId) {
        throw new Error('Select a unit before opening an inspection.')
      }

      return (
        await api.vehicleInspections.driverTodayTripEntry(currentStop.id, unitId ? { unitId } : {})
      ).data
    },
    onMutate: async () => {
      setInspectionError(null)
    },
    onSuccess: (result) => {
      window.location.assign(result.publicLink)
    },
    onError: (error: any) => {
      const message = String(error?.response?.data?.error || error?.message || 'Unable to open inspection.')
      setInspectionError(message)
    },
  })

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Today&apos;s Trip</h1>
        <p className="text-sm text-gray-500">One mobile execution view for trip status, next stop, units, load plan, and actions.</p>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400">Loading jobs…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-blue-700">Active Jobs</div>
              <div className="text-3xl font-semibold text-blue-900 mt-1">{activeJobs.length}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-green-700">Completed</div>
              <div className="text-3xl font-semibold text-green-900 mt-1">{completedJobs.length}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-gray-600">Total Jobs</div>
              <div className="text-3xl font-semibold text-gray-800 mt-1">{jobs.length}</div>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Trip Summary</h2>
            {!todayTrip && !todayJob ? (
              <p className="text-sm text-gray-400">No active jobs currently assigned.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {todayTrip?.tripNumber ?? todayJob?.jobNumber ?? todayJob?.id?.slice(0, 8)}
                  </p>
                  <p className="text-sm text-gray-500">Status: {todayTrip?.status ?? todayJob?.status}</p>
                  {todayTrip && (
                    <p className="text-xs text-gray-500 mt-1">
                      Carrier: {todayTrip.carrierOrgId ?? 'Assigned'} • Trailer: {todayTrip.trailerNumber ?? 'Pending'}
                    </p>
                  )}
                  {todayTrip && (
                    <p className="text-xs text-gray-500 mt-1">
                      Stops: {stops.length} • Units: {units.length}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!tripState.isComplete && todayTrip?.loadId && (
                    <Link to={`/driver/loads/${todayTrip.loadId}/load-plan`} className="bg-white border border-blue-200 hover:border-blue-300 text-blue-800 text-sm font-semibold px-4 py-2 rounded-lg">
                      Open Load Plan
                    </Link>
                  )}
                  {todayJob?.id && (
                    <Link to={`/driver/jobs/${todayJob.id}`} className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                      Open Job
                    </Link>
                  )}
                  <Link to="/driver/jobs" className="bg-white border border-gray-200 hover:border-gray-300 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg">
                    All Jobs
                  </Link>
                </div>
              </div>
            )}
          </div>

          {tripState.isComplete && todayTrip && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-emerald-800 uppercase tracking-wide">Trip Complete</h2>
                  <p className="text-base font-semibold text-emerald-950 mt-1">{todayTrip.tripNumber}</p>
                  <p className="text-sm text-emerald-800 mt-1">Delivery work is complete. Review proof, history, and unit summary below.</p>
                </div>
                <div className="text-right text-xs text-emerald-800">
                  <div>Trip status: {todayTrip.status}</div>
                  <div>POD status: {podStatus}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {todayJob?.id && (
                  <Link to={`/driver/jobs/${todayJob.id}`} className="px-3 py-2 rounded border border-emerald-300 text-emerald-800 text-sm font-semibold hover:bg-emerald-100">
                    Open Job History
                  </Link>
                )}
                {todayJob?.proofOfDeliveryUrl && (
                  <a href={todayJob.proofOfDeliveryUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded border border-emerald-300 text-emerald-800 text-sm font-semibold hover:bg-emerald-100">
                    View POD
                  </a>
                )}
              </div>
            </div>
          )}

          {currentStop && !tripState.isComplete && (
            <div className="bg-white border rounded-xl p-4 space-y-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Next Stop</h2>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Type</p>
                  <p className="text-sm font-semibold text-gray-900">{currentStop.stopType}</p>
                  <p className="text-xs text-gray-500 mt-1">Status: {currentStop.stopStatus ?? 'PENDING'}</p>
                  <p className="text-xs text-gray-500">Appointment: {currentStop.appointmentStatus ?? 'NOT_REQUIRED'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Location</p>
                  <p className="text-sm font-semibold text-gray-900">{currentAddress || 'Location pending'}</p>
                  <p className="text-xs text-gray-500 mt-1">Window: {currentStop.dateWindowStart ? new Date(currentStop.dateWindowStart).toLocaleString() : '—'} to {currentStop.dateWindowEnd ? new Date(currentStop.dateWindowEnd).toLocaleString() : '—'}</p>
                  <p className="text-xs text-gray-500">Contact: {currentStop.contactName ?? '—'} {currentStop.contactPhone ? `(${currentStop.contactPhone})` : ''}</p>
                  <p className="text-xs text-gray-500">Gate / lot: {currentStop.accessInstructions || currentStop.lotNotes || '—'}</p>
                  <p className="text-xs text-gray-500">Unit count: {unitCountAtStop}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <a href={mapsHref(currentAddress)} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50">Navigate</a>
                {canCall ? (
                  <a href={`tel:${contactPhone}`} className="px-3 py-2 rounded border border-green-300 text-green-700 text-sm font-semibold hover:bg-green-50">Call</a>
                ) : (
                  <button type="button" disabled className="px-3 py-2 rounded border border-gray-200 text-gray-400 text-sm">Call</button>
                )}
                {showMarkArrival && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'MARK_ARRIVAL' })} className="px-3 py-2 rounded border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50">Mark Arrival</button>}
                {showStartPickup && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'START_PICKUP' })} className="px-3 py-2 rounded border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50">Start Pickup</button>}
                {showStartDelivery && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'START_DELIVERY' })} className="px-3 py-2 rounded border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50">Start Delivery</button>}
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                {inspectionSelectionRequired && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="font-medium">Unit</span>
                    <select
                      value={selectedInspectionUnitId}
                      onChange={(event) => setSelectedInspectionUnitId(event.target.value)}
                      disabled={openInspectionMutation.isPending}
                      className="rounded border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">Select VIN</option>
                      {inspectionUnits.map((unit) => (
                        <option key={unit.id} value={unit.unit_move_id ?? ''}>
                          {displayVin(unit.vin)}{unit.trailer_slot ? ` - ${unit.trailer_slot}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {showOpenInspection && <button type="button" disabled={openInspectionMutation.isPending} onClick={() => openInspectionMutation.mutate()} className="px-3 py-2 rounded border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400">{openInspectionMutation.isPending ? 'Opening Inspection...' : 'Open Inspection'}</button>}
                {showConfirmLoaded && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'CONFIRM_LOADED', unitMoveId: selectedInspectionUnit?.unit_move_id ?? null })} className="px-3 py-2 rounded border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50">Confirm Loaded</button>}
                {showCompleteDelivery && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'COMPLETE_DELIVERY' })} className="px-3 py-2 rounded border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50">Mark Delivered</button>}
                <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'REPORT_CONDITION_ISSUE', notes: 'Driver reported condition issue from Today screen' })} className="px-3 py-2 rounded border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50">Report Condition Issue</button>
                <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'MARK_UNABLE_TO_COMPLETE', notes: 'Unable to complete stop from Today screen' })} className="px-3 py-2 rounded border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50">Mark Unable To Complete</button>
              </div>
              {inspectionError && (
                <p className="text-sm text-rose-700">{inspectionError}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {showAcknowledge && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'ACKNOWLEDGE' })} className="px-3 py-1 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50">Acknowledge</button>}
                {showEnRoute && <button type="button" onClick={() => runStopActionMutation.mutate({ stopId: currentStop.id, action: 'EN_ROUTE' })} className="px-3 py-1 rounded border border-gray-300 text-gray-700 text-xs hover:bg-gray-50">En Route</button>}
              </div>
            </div>
          )}

          {todayTrip && stops.length > 0 && !tripState.isComplete && (
            <div className="bg-white border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Current Stop Appointment</h2>
              <div className="space-y-3">
                {(stops ?? []).map((stop: any) => {
                  const isCurrentStop = stop.id === currentStop?.id
                  return (
                  <div key={stop.id} className={'border rounded-lg p-3 ' + (isCurrentStop ? 'border-blue-300 bg-blue-50/40' : 'bg-gray-50')}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{stop.stopType}</div>
                        <div className="text-xs text-gray-500">Appointment: {stop.appointmentStatus ?? 'NOT_REQUIRED'}</div>
                      </div>
                      {isCurrentStop && stop.appointmentRequired && !isTerminalStopStatus(stop) ? (
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => updateAppointmentMutation.mutate({ stopId: stop.id, appointmentStatus: 'REQUESTED' })} className="px-2 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50">Requested</button>
                          <button onClick={() => updateAppointmentMutation.mutate({ stopId: stop.id, appointmentStatus: 'CONFIRMED' })} className="px-2 py-1 text-xs rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Confirmed</button>
                          <button onClick={() => updateAppointmentMutation.mutate({ stopId: stop.id, appointmentStatus: 'RESCHEDULE_REQUESTED' })} className="px-2 py-1 text-xs rounded border border-amber-300 text-amber-700 hover:bg-amber-50">Reschedule</button>
                          <button onClick={() => updateAppointmentMutation.mutate({ stopId: stop.id, appointmentStatus: 'MISSED' })} className="px-2 py-1 text-xs rounded border border-rose-300 text-rose-700 hover:bg-rose-50">Missed</button>
                          <button onClick={() => updateAppointmentMutation.mutate({ stopId: stop.id, appointmentStatus: 'COMPLETED' })} className="px-2 py-1 text-xs rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Completed</button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">{isCurrentStop ? 'No appointment required' : 'Future stop — actions unlock when current'}</span>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="bg-white border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Upcoming Stops</h2>
              {!stops.length ? <p className="text-sm text-gray-400">No stops assigned.</p> : (
                <div className="space-y-2">
                  {stops.map((stop: any) => (
                    <div key={stop.id} className="border rounded-lg p-3">
                      <div className="text-sm font-semibold text-gray-900">{stop.stopType}</div>
                      <div className="text-xs text-gray-500 mt-1">{mapStopAddress(stop) || 'Location pending'}</div>
                      <div className="text-xs text-gray-500">Appointment: {stop.appointmentStatus ?? 'NOT_REQUIRED'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Assigned Units</h2>
              {!units.length ? <p className="text-sm text-gray-400">No units assigned yet.</p> : (
                <div className="space-y-2">
                  {units.map((unit: any) => (
                    <div key={unit.id} className="border rounded-lg p-3">
                      <div className="text-sm font-semibold text-gray-900">{displayVin(unit.vin)}</div>
                      <div className="text-xs text-gray-500">Trailer slot: {unit.trailer_slot ?? 'Unassigned'}</div>
                      <div className="text-xs text-gray-500">Pickup seq: {unit.pickup_sequence ?? '—'} • Delivery seq: {unit.delivery_sequence ?? '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!tripState.isComplete && (
          <div className="bg-white border rounded-xl p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Trailer Load Plan</h2>
            {todayTrip?.loadId ? (
              <div className="space-y-2">
                <Link to={`/driver/loads/${todayTrip.loadId}/load-plan`} className="inline-flex px-3 py-2 rounded border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50">Open Driver Load Plan</Link>
                {trailerWarnings.length > 0 ? (
                  <div className="rounded border border-amber-200 bg-amber-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-amber-700">Warnings / Needs Review</div>
                    <ul className="mt-2 space-y-1 text-xs text-amber-800">
                      {trailerWarnings.slice(0, 6).map((warning: string, index: number) => (
                        <li key={`${warning}-${index}`}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No active load-plan warnings.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Load plan will appear after assignment.</p>
            )}
          </div>
          )}

          <UnifiedTimeline title="Recent Trip Timeline" entries={timeline} defaultFilter="operations" />

          <div className="bg-white border rounded-xl p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Recent Activity</h2>
            {jobs.length === 0 ? (
              <p className="text-sm text-gray-400">No job history yet.</p>
            ) : (
              <div className="space-y-2">
                {jobs.slice(0, 6).map(job => (
                  <Link key={job.id} to={`/driver/jobs/${job.id}`} className="block border rounded-lg p-3 hover:border-blue-300 transition">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{job.jobNumber ?? job.id.slice(0, 8)}</span>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{job.status}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
